/*
 * SoMatcha tumbler charm add-on.
 *
 * The toggle records INTENT only — nothing is added to the cart when it is pressed.
 * The charm is added in the SAME request as the tumbler, when the customer presses the
 * product's own Add to Cart.
 *
 * How it integrates without touching product-form.js:
 *   product-form.js attaches its submit listener to the <form> in its constructor, in the
 *   BUBBLE phase, and builds `new FormData(this.form)` synchronously inside that handler.
 *   We attach a CAPTURE-phase listener to the parent <product-form> element, which runs
 *   earlier in the capture phase, and rewrite the form's inputs in place. We never call
 *   preventDefault or stopPropagation, so the theme's own fetch, cart-section rendering,
 *   error handling and cart-drawer refresh all run exactly as they do today.
 *
 * The [name=id] input is disabled rather than removed: product-form.js's constructor does
 * `this.variantIdInput.disabled = false` on it and would throw on a Theme Editor re-render
 * if it were gone. Disabling excludes it from FormData without removing it from the DOM.
 *
 * Accepted trade-off: while the add-on is active the form posts items[] instead of a plain
 * `id`, so product-form.js's `formData.get('id')` is null and its analytics payload carries
 * a null variant id. Recorded in the Technical Debt Log in DESIGN.md.
 */
if (!customElements.get('sm-tumbler-addon')) {
  class SmTumblerAddon extends HTMLElement {
    constructor() {
      super();
      this.selected = false;
      // True only for a submission where WE injected charm inputs. This is what keeps the
      // retry path from ever touching a plain sold-out tumbler.
      this.pendingAddon = false;
      this.retried = false;
      this.injected = [];
    }

    connectedCallback() {
      this.toggle = this.querySelector('.sm-addon__toggle');
      this.messageEl = this.querySelector('.sm-addon__message');
      this.charmVariantId = this.dataset.charmVariantId;
      this.labelAdd = this.dataset.labelAdd || '';
      this.labelAdded = this.dataset.labelAdded || '';
      this.msgUnavailable = this.dataset.msgUnavailable || '';

      /*
       * Bind by the buy-buttons form's own id, NOT by position.
       *
       * This previously did closest('.shopify-section').querySelector('product-form'),
       * i.e. the FIRST product-form in the section. But main-product can contain more than
       * one: its `complementary` block renders card-product, which emits its own
       * product-form. Block order is merchant-controlled in the Theme Editor, so placing
       * complementary above buy_buttons would have silently bound the add-on to a
       * recommended product's form and attached the charm to the wrong product. Looking
       * the form up by id is immune to block ordering.
       */
      this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
      this.productForm = this.form ? this.form.closest('product-form') : null;

      if (!this.toggle || !this.form || !this.productForm || !this.charmVariantId) return;

      this.submitButton = this.productForm.querySelector('[type="submit"]');

      // Presentation-only dialog, owned by assets/sm-charm-dialog.js. Looked up by id
      // because ModalDialog relocates it to document.body, putting it outside this
      // element's subtree. Absent when the merchant has no charm — see the third branch
      // of onSubmitCapture, which then simply proceeds untouched.
      this.dialog = this.dataset.dialogId ? document.getElementById(this.dataset.dialogId) : null;
      this.alreadyOffered = false;
      if (this.dialog) {
        this.dialog.addEventListener('sm-charm:choice', this.onDialogChoice.bind(this));
      }

      this.onToggle = this.onToggle.bind(this);
      this.onSubmitCapture = this.onSubmitCapture.bind(this);
      this.toggle.addEventListener('click', this.onToggle);
      this.productForm.addEventListener('submit', this.onSubmitCapture, true);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError.bind(this));
        this.unsubscribeUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }
    }

    disconnectedCallback() {
      if (this.productForm) this.productForm.removeEventListener('submit', this.onSubmitCapture, true);
      if (this.unsubscribeError) this.unsubscribeError();
      if (this.unsubscribeUpdate) this.unsubscribeUpdate();
      this.restoreForm();
    }

    onToggle() {
      this.setSelected(!this.selected);
      // A fresh press is fresh intent, so allow the one retry again.
      this.retried = false;
      this.hideMessage();
    }

    setSelected(next) {
      this.selected = next;
      this.toggle.setAttribute('aria-pressed', String(next));
      this.toggle.textContent = next ? this.labelAdded : this.labelAdd;
    }

    /*
     * Capture phase on <product-form>, ahead of product-form.js's own handler.
     *
     * ONE handler owns all three outcomes. A second capture listener on this same element
     * would be ordered only by script-tag position, could not be suppressed with
     * stopPropagation (that does not stop same-element listeners), and could cancel a
     * submission after this handler had already mutated the form — a form-state bug on the
     * store's main purchase path.
     *
     *   1. toggle ON            -> inject items[] and proceed          (unchanged)
     *   2. toggle OFF, offered  -> proceed untouched                   (unchanged)
     *   3. toggle OFF, not yet  -> cancel and offer the charm          (new)
     */
    onSubmitCapture(event) {
      this.restoreForm();

      if (!this.selected) {
        // Branch 3: the customer has not chosen the charm and has not been asked yet.
        // Cancel this submission outright and offer it; the dialog's choice re-submits.
        if (this.dialog && !this.alreadyOffered) {
          event.preventDefault();
          event.stopPropagation();
          this.alreadyOffered = true;
          this.dialog.open(this.submitButton);
          return;
        }
        // Branch 2, unchanged: add-on off, form exactly as the theme shipped it.
        this.pendingAddon = false;
        return;
      }

      // Branch 1, unchanged.
      if (!this.applyItems()) {
        this.pendingAddon = false;
        return;
      }
      this.pendingAddon = true;
      // product-form.js builds FormData synchronously in the handler that runs straight
      // after this one, so restoring on the next macrotask leaves the DOM back to normal
      // without affecting the request that was just captured.
      setTimeout(() => this.restoreForm(), 0);
    }

    /*
     * The dialog reported a decision. Rather than duplicating any cart logic, this simply
     * sets the toggle to match the choice and re-submits: the re-submission then falls
     * through branch 1 or branch 2 above, which are the same code paths that were already
     * shipped and tested. alreadyOffered is true by now, so branch 3 cannot fire again and
     * the dialog cannot re-open from the re-submission.
     */
    onDialogChoice(event) {
      const withCharm = event && event.detail ? event.detail.withCharm : false;
      if (withCharm !== this.selected) this.setSelected(withCharm);
      this.submitAfterChoice();
    }

    /*
     * Guarded entry from the dialog. submissionStarted mirrors the card popup's discipline:
     * set immediately before dispatching so a dismissal that races the request can never
     * produce a second add, and cleared when the outcome arrives.
     */
    submitAfterChoice() {
      if (this.submissionStarted) return;
      this.dispatchSubmit();
    }

    /*
     * The SINGLE requestSubmit() call site in this file. Both the dialog choice and the
     * charm-unavailable retry go through here, so there is exactly one place where this
     * file can put a submission on the wire.
     */
    dispatchSubmit() {
      if (!this.form || typeof this.form.requestSubmit !== 'function') return;
      this.submissionStarted = true;
      this.form.requestSubmit();
    }

    /*
     * Look a control up through the form's own elements collection, NOT with
     * form.querySelector.
     *
     * Origin renders the quantity selector in a separate block, so the quantity input is
     * NOT a DOM descendant of <form> — it is associated to it by the HTML `form` attribute
     * (`form="{{ product_form_id }}"` in the quantity_selector block). A descendant query
     * therefore returns null for it, while `form.elements` includes form-associated
     * controls declared anywhere in the document, which is exactly what FormData collects.
     */
    field(name) {
      if (!this.form || !this.form.elements) return null;
      const found = this.form.elements.namedItem(name);
      if (!found) return null;
      // namedItem returns a RadioNodeList when several controls share the name.
      return typeof found.length === 'number' && !('value' in found) ? found[0] : found;
    }

    applyItems() {
      const idInput = this.field('id');
      const qtyInput = this.field('quantity');
      if (!idInput || !idInput.value) return false;

      // Read at submit time, from the live control, so any change the customer makes
      // after page load or after toggling is honoured.
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      idInput.disabled = true;
      if (qtyInput) qtyInput.disabled = true;

      this.injected = [
        this.hiddenInput('items[0][id]', idInput.value),
        this.hiddenInput('items[0][quantity]', String(quantity)),
        this.hiddenInput('items[1][id]', this.charmVariantId),
        // The charm quantity MATCHES the tumbler quantity (Mohammad, 2026-08-23 — this
        // reverses the earlier "charm fixed at 1" decision): 3 tumblers, 3 charms.
        this.hiddenInput('items[1][quantity]', String(quantity)),
      ];
      this.injected.forEach((input) => this.form.appendChild(input));
      return true;
    }

    hiddenInput(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.dataset.smAddon = 'true';
      return input;
    }

    /* Must use the same form-association-aware lookup as applyItems, or the quantity
       control would be disabled without ever being re-enabled, leaving the customer
       unable to change it. */
    restoreForm() {
      if (!this.form) return;
      this.form.querySelectorAll('[data-sm-addon]').forEach((input) => input.remove());
      const idInput = this.field('id');
      const qtyInput = this.field('quantity');
      if (idInput) idInput.disabled = false;
      if (qtyInput) qtyInput.disabled = false;
      this.injected = [];
    }

    onCartUpdate() {
      // Successful add — this submission is finished, so it can no longer trigger a retry.
      this.pendingAddon = false;
      this.retried = false;
      this.submissionStarted = false;
      // Defensive: applyItems disables the id and quantity controls, and the scheduled
      // restore is what re-enables them. Restoring here too means a missed timer can
      // never strand the customer with a disabled quantity selector.
      this.restoreForm();
      this.closeDialogAndRearm();
    }

    /*
     * alreadyOffered LIFECYCLE — set in branch 3 of onSubmitCapture, immediately before the
     * dialog opens; cleared only here, and only once the dialog is actually closed.
     *
     * That ordering is what makes a loop impossible. While the dialog is open the flag
     * stays true, so the re-submission triggered by the customer's choice falls through
     * branch 1 or 2 and cannot re-open it. Clearing only after the dialog is closed also
     * means a cartUpdate raised by some OTHER form on the page — a card add, say — cannot
     * re-arm us mid-choice and cause a second, spurious opening.
     *
     * Clearing it on a completed outcome is also what stops it blocking a second genuine
     * purchase: press Add to Cart again later in the same page session and the charm is
     * offered again, exactly as on the first press.
     */
    closeDialogAndRearm() {
      if (this.dialog && this.dialog.hasAttribute('open')) this.dialog.close();
      if (!this.dialog || !this.dialog.hasAttribute('open')) this.alreadyOffered = false;
    }

    onCartError(data) {
      // Only ever act on a submission where we injected the charm. A plain sold-out tumbler,
      // or a failure from any other product form on the page, leaves this false and the
      // theme's existing error behaviour runs untouched.
      if (!this.pendingAddon) {
        // No charm was involved. If this was OUR product-only submission, close the dialog
        // so the theme's own error is visible on the buy button, and re-arm for a retry.
        // When submissionStarted is false we were not involved at all and this is a no-op,
        // exactly as before.
        if (this.submissionStarted) {
          this.submissionStarted = false;
          this.closeDialogAndRearm();
        }
        return;
      }
      if (data && data.source && data.source !== 'product-form') return;

      this.pendingAddon = false;
      if (this.retried) return; // one retry only, never a loop
      this.retried = true;

      // The request was atomic, so nothing was added. The charm is the only line we added to
      // an otherwise-valid submission, so treat the failure as "charm unavailable": drop it,
      // tell the customer, and re-submit the tumbler on its own.
      this.setSelected(false);
      // Route the message to whichever surface the customer is looking at: the dialog when
      // it is open, otherwise the block's own message line.
      if (this.dialog && this.dialog.hasAttribute('open')) {
        this.dialog.showMessage(this.msgUnavailable);
      } else {
        this.showMessage(this.msgUnavailable);
      }

      setTimeout(() => {
        this.restoreForm();
        // Deferred so product-form.js has finished its error handling and its .finally has
        // cleared the loading state and the aria-disabled flag before we submit again.
        this.dispatchSubmit();
      }, 0);
    }

    showMessage(text) {
      if (!this.messageEl || !text) return;
      this.messageEl.textContent = text;
      this.messageEl.removeAttribute('hidden');
    }

    hideMessage() {
      if (!this.messageEl) return;
      this.messageEl.textContent = '';
      this.messageEl.setAttribute('hidden', '');
    }
  }

  customElements.define('sm-tumbler-addon', SmTumblerAddon);
}
